import { RULE_LOG_TYPE } from "./constants";

export const BID_V2_LOG_TYPE = "Automation";
export const BID_V2_PARENT_RULE_TYPE = "rule_tracking";
export const BID_V2_CHILD_RULE_TYPE = "rule_entity_tracking";
export const BID_V2_LOGS_PARENT_SKELETON_ROWS = 6;
export const BID_V2_LOGS_CHILD_SKELETON_ROWS = 3;
export const BID_V2_LOGS_DEFAULT_PAGE_SIZE = 50;
export const BID_V2_LOGS_PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];

export const BID_V2_LOG_COLUMNS = [
  { key: "timestamp", header: "Timestamp", minWidth: 130 },
  { key: "ruleTask", header: "Rule Task", minWidth: 130 },
  { key: "campaign", header: "Campaign", minWidth: 120 },
  { key: "adGroup", header: "Ad Group", minWidth: 120 },
  { key: "targeting", header: "Targeting", minWidth: 170 },
  { key: "event", header: "Event", minWidth: 160 },
  { key: "bidInfo", header: "Bid Info", minWidth: 180 },
  { key: "triggeredBy", header: "Triggered By/Config", minWidth: 180 },
];

function parseJsonField(value) {
  if (value == null || value === "" || value === "null") return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function display(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function infoLine(label, value) {
  const shown = display(value);
  return `${label}: ${shown === "" ? "—" : shown}`;
}

function normalizeUtcTimestamp(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return `${raw}Z`;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(raw)) {
    return `${raw.replace(" ", "T")}Z`;
  }
  return raw;
}

export function formatLogTimestampUtcToIst(value) {
  if (value == null || value === "") return "";
  const normalized = normalizeUtcTimestamp(value);
  if (!normalized) return "";
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return display(value);

  const datePart = date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${datePart},\n${timePart}`;
}

function readActionConfig(log) {
  const ruleConfig = parseJsonField(log?.rule_config);
  const action =
    ruleConfig?.ruleActionConfig?.entityActionConfig ??
    ruleConfig?.rule_action_config?.entity_action_config ??
    {};
  return {
    actionType: action.actionType ?? action.action_type ?? "",
    percentageChange: action.percentageChange ?? action.percentage_change ?? "",
  };
}

function formatBidInfo(extra, action) {
  const before = extra?.budgetBefore ?? extra?.budget_before;
  const after = extra?.budgetAfter ?? extra?.budget_after;
  const change = action.percentageChange;
  const changeLabel =
    change === "" || change == null
      ? ""
      : `${change}${String(change).includes("%") ? "" : "%"}`;

  if (!action.actionType && changeLabel === "" && before == null && after == null) {
    return "";
  }

  return [
    infoLine("Type", action.actionType),
    infoLine("Change", changeLabel),
    infoLine("Before", before),
    infoLine("After", after),
  ].join("\n");
}

function formatCountPair(impacted, evaluated) {
  return [infoLine("Impacted", impacted), infoLine("Evaluated", evaluated)].join("\n");
}

function collectHourOfDayValues(node, acc) {
  if (!node || typeof node !== "object") return;
  if (node.type === "RULE_ITEM" && node.member === "hour_of_day" && Array.isArray(node.values)) {
    node.values.forEach((value) => acc.push(String(value)));
    return;
  }
  ["and", "or"].forEach((key) => {
    if (Array.isArray(node[key])) node[key].forEach((child) => collectHourOfDayValues(child, acc));
  });
  ["dimensions", "measures", "filters", "query", "cubeFilters", "cube_filters"].forEach((key) => {
    if (node[key] && typeof node[key] === "object") collectHourOfDayValues(node[key], acc);
  });
}

function hourSlotLabel(values) {
  const hours = [...new Set(values.map((value) => Number(value)).filter((value) => Number.isFinite(value)))].sort(
    (a, b) => a - b
  );
  if (!hours.length) return "";
  const label = (hour) => (hour === 24 ? "24:00" : `${String(hour).padStart(2, "0")}:00`);
  return `${label(hours[0])} - ${label(hours[hours.length - 1])}`;
}

function hourSlotFromRuleConfig(ruleConfigRaw) {
  const ruleConfig = parseJsonField(ruleConfigRaw);
  const cubeRaw =
    ruleConfig?.entityMetricFilter?.cubeFilters ??
    ruleConfig?.entity_metric_filter?.cube_filters ??
    "";
  const cubeFilters = parseJsonField(cubeRaw);
  const hours = [];
  collectHourOfDayValues(cubeFilters, hours);
  return hourSlotLabel(hours);
}

function formatChildRuleTask(log) {
  const name = display(log?.rule_task_name);
  const slot = hourSlotFromRuleConfig(log?.rule_config);
  if (!slot) return name;
  if (!name) return slot;
  return `${name}\n${slot}`;
}

export function mapBidLogToRow(log, { isChild = false } = {}) {
  const decision = parseJsonField(log?.decision);
  const extra = {
    ...(decision?.extraInfo ?? {}),
    ...parseJsonField(log?.extra_info_json),
  };
  const action = readActionConfig(log);

  const base = {
    rowKey: String(log?.id ?? `${log?.track_id ?? ""}-${log?.entity_id ?? ""}-${log?.created_at ?? ""}`),
    timestamp: formatLogTimestampUtcToIst(log?.created_at),
    event: display(extra?.event ?? log?.message),
    triggeredBy: display(log?.triggered_by),
    trackId: log?.track_id ?? "",
  };

  if (!isChild) {
    return {
      ...base,
      ruleTask: "",
      campaign: formatCountPair(extra?.campaignsImpacted, extra?.campaignsEvaluated),
      adGroup: formatCountPair(extra?.adsetsImpacted, extra?.adsetsEvaluated),
      targeting: display(extra?.effectiveChange ?? log?.effective_change),
      bidInfo: "",
      ruleConfig: parseJsonField(log?.rule_config),
    };
  }

  return {
    ...base,
    ruleTask: formatChildRuleTask(log),
    campaign: display(extra?.campaign_name ?? log?.parent_entity_name),
    adGroup: display(extra?.adgroup_name),
    targeting: display(extra?.target_name ?? log?.entity_name),
    bidInfo: formatBidInfo(extra, action),
    status: display(extra?.executionStatus ?? extra?.outcome ?? log?.message),
  };
}

export function buildParentLogsFiltering(ruleName) {
  const filtering = [
    { field_name: "type", operator: "EQUALS", value: RULE_LOG_TYPE },
  ];
  if (ruleName) {
    filtering.push({ field_name: "rule_name", operator: "EQUALS", value: ruleName });
  }
  return filtering;
}

export function buildChildLogsFiltering(trackId, ruleName) {
  const filtering = [
    { field_name: "type", operator: "EQUALS", value: RULE_LOG_TYPE },
    { field_name: "track_id", operator: "EQUALS", value: trackId },
  ];
  if (ruleName) {
    filtering.push({ field_name: "rule_name", operator: "EQUALS", value: ruleName });
  }
  return filtering;
}

export function buildBidAutomationLogsUrl(
  clientId,
  { source, filtering, ruleType, page = 0, limit = BID_V2_LOGS_DEFAULT_PAGE_SIZE }
) {
  const params = new URLSearchParams({
    source,
    type: BID_V2_LOG_TYPE,
    rule_type: ruleType,
    filtering: JSON.stringify(filtering),
    page: String(page),
    limit: String(limit),
  });
  return `${process.env.REACT_APP_ADS_API}/client/${clientId}/logs?${params.toString()}`;
}

export function parseLogsApiList(responseData) {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
}

export function parseLogsApiResponse(responseData) {
  const totalFromApi =
    responseData?.total_count ??
    responseData?.totalCount ??
    responseData?.data?.total_count ??
    responseData?.data?.totalCount ??
    null;
  return {
    list: parseLogsApiList(responseData),
    totalCount: totalFromApi,
  };
}

export function resolveLogsTotalCount(totalFromApi, listLength, zeroBasedPage, pageLimit) {
  if (totalFromApi !== null && totalFromApi !== undefined && totalFromApi !== "") {
    const parsed = Number(totalFromApi);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (listLength < pageLimit) {
    return zeroBasedPage * pageLimit + listLength;
  }
  return zeroBasedPage * pageLimit + listLength + 1;
}
